import React, { useState } from 'react';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import './OrderDetailsModal.css';

const SIZES = ['YS', 'YM', 'YL', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

const OrderDetailsModal = ({ order, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editData, setEditData] = useState({});
  const [notesCharCount, setNotesCharCount] = useState(0);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const isAdmin = user?.role === 'admin';

  React.useEffect(() => {
    if (order) {
      setEditData({
        customer_name: order.customer_name,
        customer_phone: order.customer_phone,
        notes: order.notes || '',
        items: (order.items || []).map((i) => ({ ...i })),
      });
      setNotesCharCount(order.notes?.length || 0);
      setIsEditing(false);
      setError(null);
    }
  }, [order]);

  if (!isOpen || !order) return null;

  const getShirtCount = (o) => (o.items || []).reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0);
  const getTotal = (o) => getShirtCount(o) * parseFloat(o.price || 0);

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    if (field === 'notes') setNotesCharCount(value.length);
  };

  const handleItemChange = (index, field, value) => {
    setEditData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addItem = () => {
    setEditData((prev) => ({
      ...prev,
      items: [...prev.items, { name_on_back: '', size: '', quantity: 1 }],
    }));
  };

  const removeItem = (index) => {
    setEditData((prev) => ({
      ...prev,
      items: prev.items.length === 1 ? prev.items : prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.put(`/spikes-gear/orders/${order.id}`, {
        customer_name: editData.customer_name,
        customer_phone: editData.customer_phone,
        items: editData.items.map((i) => ({
          name_on_back: i.name_on_back,
          size: i.size,
          quantity: parseInt(i.quantity),
        })),
        notes: editData.notes,
      });

      setIsEditing(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      setLoading(true);
      setError(null);

      try {
        await api.delete(`/spikes-gear/orders/${order.id}`);
        onSuccess?.();
        onClose();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete order');
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await api.patch(`/spikes-gear/orders/${order.id}/status`, { order_status: newStatus });
      order.order_status = newStatus;
      onSuccess?.();
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Order' : 'Order Details'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          {!isEditing ? (
            // VIEW MODE
            <>
              <div className="order-header-info">
                <div className="info-item">
                  <span className="label">Order ID</span>
                  <span className="value">{order.id}</span>
                </div>
                <div className="info-item">
                  <span className="label">Created</span>
                  <span className="value">{formatDate(order.created_at)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Status</span>
                  <select
                    className="status-select-modal"
                    value={order.order_status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={statusUpdating}
                  >
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="picked up">Picked Up</option>
                  </select>
                </div>
              </div>

              {/* Customer Section */}
              <div className="modal-section">
                <h4>Customer Info</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Name</span>
                    <span className="value">{order.customer_name}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Phone</span>
                    <span className="value">{order.customer_phone}</span>
                  </div>
                </div>
              </div>

              {/* Shirts Section */}
              <div className="modal-section">
                <h4>Shirts ({getShirtCount(order)} total)</h4>
                <div className="shirts-list">
                  {(order.items || []).map((item, index) => (
                    <div className="shirt-row" key={index}>
                      <span className="shirt-name">{item.name_on_back}</span>
                      <span className="shirt-size">Size {item.size}</span>
                      <span className="shirt-qty">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="info-grid" style={{ marginTop: '16px' }}>
                  <div className="info-item">
                    <span className="label">Price per Shirt</span>
                    <span className="value">${parseFloat(order.price).toFixed(2)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Order Total</span>
                    <span className="value">${getTotal(order).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              {order.sales_person || order.notes ? (
                <div className="modal-section">
                  {order.sales_person && (
                    <div className="info-item">
                      <span className="label">Salesperson</span>
                      <span className="value">{order.sales_person}</span>
                    </div>
                  )}
                  {order.notes && (
                    <div className="info-item full-width">
                      <span className="label">Notes</span>
                      <span className="value notes-text">{order.notes}</span>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          ) : (
            // EDIT MODE
            <form className="edit-form">
              <div className="form-section-edit">
                <h4>Customer Info</h4>
                <div className="form-row-edit">
                  <div className="form-group-edit">
                    <label>Customer Name</label>
                    <input
                      type="text"
                      value={editData.customer_name || ''}
                      onChange={(e) => handleEditChange('customer_name', e.target.value)}
                      maxLength="255"
                    />
                  </div>
                  <div className="form-group-edit">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editData.customer_phone || ''}
                      onChange={(e) => handleEditChange('customer_phone', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section-edit">
                <h4>Order Details</h4>
                <div className="form-group-edit">
                  <label>Price per Shirt</label>
                  <span className="value">${parseFloat(order.price).toFixed(2)} fixed</span>
                </div>

                <div className="items-table">
                  <div className="items-table-header">
                    <span>Name on Back</span>
                    <span>Size</span>
                    <span>Qty</span>
                    <span></span>
                  </div>
                  {(editData.items || []).map((item, index) => (
                    <div className="items-table-row" key={index}>
                      <input
                        type="text"
                        value={item.name_on_back}
                        onChange={(e) => handleItemChange(index, 'name_on_back', e.target.value)}
                        maxLength="100"
                      />
                      <select
                        value={item.size}
                        onChange={(e) => handleItemChange(index, 'size', e.target.value)}
                      >
                        <option value="">Size</option>
                        {SIZES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-remove-item"
                        onClick={() => removeItem(index)}
                        disabled={editData.items.length === 1}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn-add-item" onClick={addItem}>
                  + Add Another Shirt
                </button>

                <div className="form-group-edit" style={{ marginTop: '16px' }}>
                  <label>Notes ({notesCharCount}/1000)</label>
                  <textarea
                    value={editData.notes || ''}
                    onChange={(e) => handleEditChange('notes', e.target.value)}
                    maxLength="1000"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </form>
          )}
        </div>

        <div className="modal-footer">
          {!isEditing ? (
            <>
              {isAdmin && (
                <>
                  <button className="btn-edit" onClick={() => setIsEditing(true)} disabled={loading}>
                    Edit Order
                  </button>
                  <button className="btn-delete" onClick={handleDelete} disabled={loading}>
                    Delete Order
                  </button>
                </>
              )}
              <button className="btn-close" onClick={onClose} disabled={loading}>
                Close
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-cancel"
                onClick={() => {
                  setIsEditing(false);
                  setError(null);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveEdit} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
